import { Skeleton } from '@/components/ui/skeleton';


export function LoadingSkeletonOutline() {
    return (
        <div className="flex flex-1 flex-col">
            {/* Sticky Header Skeleton */}
            <div className="sticky top-12 z-10 bg-background/95 backdrop-blur border-b border-border">
                <div className="px-4 py-3 lg:px-6">
                    <div className="flex items-center justify-between gap-4">
                        <Skeleton className="h-10 w-48" /> {/* Book selector */}
                        <Skeleton className="h-6 w-24" />  {/* Summary toggle */}
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="@container/main flex flex-1 flex-col gap-2">
                <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
                    {/* Outline Tree Skeleton */}
                    <div className="space-y-1">
                        {/* Level 0 - Root passages */}
                        <OutlineNodeSkeleton depth={0} />

                        {/* Level 1 - Subpassages under first root */}
                        <OutlineNodeSkeleton depth={1} />
                        <OutlineNodeSkeleton depth={1} />

                        {/* Level 2 - Sub-subpassages */}
                        <OutlineNodeSkeleton depth={2} />
                        <OutlineNodeSkeleton depth={2} />

                        {/* More Level 0 */}
                        <OutlineNodeSkeleton depth={0} />

                        {/* Level 1 under second root */}
                        <OutlineNodeSkeleton depth={1} />

                        {/* Another Level 0 */}
                        <OutlineNodeSkeleton depth={0} />

                        {/* More Level 1 */}
                        <OutlineNodeSkeleton depth={1} />
                        <OutlineNodeSkeleton depth={1} />

                        {/* Final Level 0 */}
                        <OutlineNodeSkeleton depth={0} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function OutlineNodeSkeleton({ depth }: { depth: number }) {
    // Create responsive indent classes
    const getIndentClass = (depth: number) => {
        const classes = [
            'ml-0 sm:ml-0', // depth 0
            'ml-4 sm:ml-8', // depth 1
            'ml-8 sm:ml-16', // depth 2
            'ml-12 sm:ml-24', // depth 3
        ];
        return classes[depth] || 'ml-16 sm:ml-32';
    };

    return (
        <div className="relative">
            {/* Content with indent */}
            <div className={`${getIndentClass(depth)}`}>
                <div className={`transition-all duration-200 cursor-pointer group bg-gradient-to-b ${
                    depth === 0
                        ? 'from-slate-50/20 to-slate-100/20'
                        : depth === 1
                        ? 'from-slate-50/15 to-slate-100/15'
                        : 'from-slate-50/10 to-slate-100/10'
                } ${
                    depth === 0 ? 'border-2 border-primary/20' : ''
                } ${depth === 1 ? 'border-l-4 border-l-primary/40' : ''} ${
                    depth >= 2 ? 'border-l-2 border-l-muted-foreground/30' : ''
                } rounded-lg border p-3`}>
                        <div className="flex items-start gap-2">
                            {/* Icon skeleton */}
                            <Skeleton className="w-4 h-4 mt-0.5 flex-shrink-0" />

                            {/* Content skeleton */}
                            <div className="flex-1 min-w-0">
                                {/* Title and verse reference */}
                                <div className="flex items-center gap-2 mb-2">
                                    <Skeleton className="h-5 w-20 rounded-md" /> {/* Verse reference */}
                                    <Skeleton className={`h-5 flex-1 ${
                                        depth === 0 ? 'max-w-80' : depth === 1 ? 'max-w-60' : 'max-w-48'
                                    }`} /> {/* Title */}
                                </div>

                                {/* Badges skeleton - desktop */}
                                <div className="hidden sm:flex sm:items-center sm:justify-between">
                                    <div /> {/* Spacer */}
                                    <div className="flex gap-1">
                                        <Skeleton className="h-5 w-16" /> {/* Badge 1 */}
                                        <Skeleton className="h-5 w-20" /> {/* Badge 2 */}
                                        <Skeleton className="h-5 w-24" /> {/* Passage count */}
                                    </div>
                                </div>

                                {/* Badges skeleton - mobile */}
                                <div className="sm:hidden flex flex-wrap gap-1 mt-1">
                                    <Skeleton className="h-4 w-16" /> {/* Badge 1 */}
                                    <Skeleton className="h-4 w-20" /> {/* Badge 2 */}
                                    <Skeleton className="h-4 w-24" /> {/* Passage count */}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    );
}

export function LoadingSkeletonPassageDetails() {
    return (
        <div className="relative bg-cover bg-center h-full">
            <div className="relative">
                {/* Title row: left title bar, right info button */}
                <div className="flex w-full items-center justify-between mt-2 px-3 sm:px-4">
                    <Skeleton className="h-8 sm:h-10 lg:h-12 w-3/5 rounded-md" />
                    <Skeleton className="h-8 w-8 rounded-full" />
                </div>

                {/* Content area: matches PassageDetails container with borders */}
                <div className="p-2 pb-30 border-t border-b mt-2">
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-[92%] rounded" />
                        <Skeleton className="h-4 w-[88%] rounded" />
                        <Skeleton className="h-4 w-[94%] rounded" />
                        <Skeleton className="h-4 w-[76%] rounded" />
                        <Skeleton className="h-4 w-[90%] rounded" />
                        <Skeleton className="h-4 w-[84%] rounded" />
                        <Skeleton className="h-4 w-[70%] rounded" />
                        <Skeleton className="h-4 w-[95%] rounded" />
                        <Skeleton className="h-4 w-[80%] rounded" />
                        <Skeleton className="h-4 w-[66%] rounded" />
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Get paragraphs from a TranslationByParagraph that fall within a passage's verse range.
 *
 * Performance optimization: If passage has pre-calculated paragraphRange for the version,
 * uses direct array slicing O(1). Otherwise falls back to startPositions index to jump to
 * the relevant chapter, then iterates forward with early termination.
 *
 * @param paragraphs - Array of paragraphs from TranslationByParagraph
 * @param passage - Passage with book and ref (e.g., "1:1-8" or "1:1-2:8")
 * @param startPositions - Optional index mapping book->chapter->paragraphIndex for fast lookup
 * @param excludeHeaders - Optional flag to filter out headers (h > 0) from the result
 * @param version - Optional translation version (e.g., 'csb', 'esv') to use pre-calculated ranges
 * @returns Filtered array of paragraphs that overlap with the passage's verse range
 */
export function getParagraphsFromPassage(
    paragraphs: TranslationParagraph[],
    passage: Passage,
    version: string,
    excludeHeaders?: boolean,
): TranslationParagraph[] {
    if (!version || !passage.paragraphRange?.[version]) {
        throw new Error(`Could not gert paragraphs for passage ${passage.id} in version ${version}`);
    }

    const { startIndex, endIndex } = passage.paragraphRange[version];
    const result = paragraphs.slice(startIndex, endIndex + 1);

    // if there is a title right before this, include it
    if (paragraphs[startIndex - 1]?.h > 0) {
        result.unshift(paragraphs[startIndex - 1]);
    }

    // Filter out headers if requested
    if (excludeHeaders) {
        return result.filter(p => !p.h || p.h === 0);
    }

    // don't allow the last item to be a header
    return result.filter((p, ndx) => {
        return !p.h || ndx < result.length - 1;
    });
}

